import { Fragment, useMemo, useState } from 'react';
import { useQuery } from '../../hooks/useQuery';
import { useDirtyFlag } from '../../hooks/useDirtyGuard';
import { api, type PlanVersion, type DriverDef } from '../../lib/api';
import { Spinner, ActionButton, Select } from '../ui';
import { SaveStatus, useSaveState } from '../SaveStatus';
import { toInputValue, fromInputValue, unitSuffix } from '../../lib/fpaDrivers';

/**
 * Planning v2 Drivers grid — the driver-value editing surface, in the same
 * grid idiom as the Budget Grid (spec §5). Rows are drivers grouped by their
 * group; there's a "Global" column (the all-months value) plus one column per
 * month; a department pivot selects which slice you edit. Values are unit-aware
 * (ratios shown as %). Edits buffer locally and commit in one bulk upsert
 * through PATCH /plans/:id/assumptions, which validates against fpa_drivers and
 * respects the (key, month?, dept?) precedence chain — so setting only the
 * Global cell already covers every month.
 */

function monthsOf(startYmd: string, endYmd: string): string[] {
  const s = startYmd.slice(0, 7).split('-').map(Number);
  const e = endYmd.slice(0, 7).split('-').map(Number);
  const out: string[] = [];
  let y = s[0]!, m = s[1]!;
  while (y < e[0]! || (y === e[0]! && m <= e[1]!)) { out.push(`${y}-${String(m).padStart(2, '0')}`); m++; if (m > 12) { m = 1; y++; } }
  return out;
}

const DEFAULT_DEPT = '__default__'; // department_id = null (all-department default)

export default function PlanDriversGrid({ plan, canWrite }: { plan: PlanVersion; canWrite: boolean }) {
  const planId = plan.id;
  const months = useMemo(() => monthsOf(plan.horizon_start, plan.horizon_end), [plan.horizon_start, plan.horizon_end]);
  const driversQ = useQuery(() => api.fpa.driverRegistry(), [planId]);
  const asmptQ = useQuery(() => api.fpa.plans.assumptions(planId), [planId]);
  const deptsQ = useQuery(() => api.fpa.departments(), [planId]);

  const [dept, setDept] = useState<string>(DEFAULT_DEPT);
  const [pending, setPending] = useState<Record<string, string>>({}); // `${key}|${month??''}|${deptId??''}` → raw input
  const save = useSaveState();

  // Hooks stay above the loading return.
  const dirty = Object.keys(pending).length > 0;
  useDirtyFlag(dirty && canWrite, 'the drivers grid');

  if (driversQ.isLoading || asmptQ.isLoading) return <Spinner />;
  const drivers = (driversQ.data?.drivers ?? []).filter((d) => d.is_active !== false);
  const departments = deptsQ.data?.departments ?? [];
  const deptId = dept === DEFAULT_DEPT ? null : dept;
  const editableCanWrite = canWrite && plan.status === 'draft';

  // Stored assumption lookup: `${key}|${month??''}|${deptId??''}` → value_numeric.
  const stored = new Map<string, number>();
  for (const a of asmptQ.data?.assumptions ?? []) {
    if (a.value_numeric == null) continue;
    stored.set(`${a.assumption_key}|${a.period_month ?? ''}|${a.department_id ?? ''}`, a.value_numeric);
  }
  const bkey = (key: string, month: string | null, d: string | null) => `${key}|${month ?? ''}|${d ?? ''}`;

  // Raw string shown in a cell: pending edit, else stored value (unit-formatted).
  function rawFor(driver: DriverDef, month: string | null): string {
    const d = driver.scope === 'company' ? null : deptId;
    const k = bkey(driver.key, month, d);
    if (k in pending) return pending[k]!;
    const v = stored.get(k);
    return v == null ? '' : toInputValue(driver.unit, v);
  }
  // Effective value (for the placeholder on empty month cells): month → global → default.
  function effective(driver: DriverDef): string {
    const d = driver.scope === 'company' ? null : deptId;
    const g = pending[bkey(driver.key, null, d)] ?? (stored.has(bkey(driver.key, null, d)) ? toInputValue(driver.unit, stored.get(bkey(driver.key, null, d))!) : '');
    if (g !== '') return g;
    return driver.default_value == null ? '' : toInputValue(driver.unit, driver.default_value);
  }
  function setCell(driver: DriverDef, month: string | null, raw: string) {
    const d = driver.scope === 'company' ? null : deptId;
    setPending((p) => ({ ...p, [bkey(driver.key, month, d)]: raw }));
    save.clear();   // editing again retires the last "Saved" line
  }
  function fillRight(driver: DriverDef) {
    // Materialise the Global value into every month cell (buffered).
    const g = rawFor(driver, null);
    setPending((p) => { const n = { ...p }; for (const m of months) n[bkey(driver.key, m, driver.scope === 'company' ? null : deptId)] = g; return n; });
  }

  const byKey = new Map(drivers.map((d) => [d.key, d]));

  async function runSave() {
    const entries = Object.entries(pending).map(([k, raw]) => {
      const [key, month, d] = k.split('|');
      const driver = byKey.get(key!);
      const value = driver ? fromInputValue(driver.unit, raw) : (raw.trim() === '' ? null : Number(raw));
      return { assumption_key: key!, period_month: month || null, department_id: d || null, value_numeric: value };
    });
    if (!entries.length) return;
    // setAssumptions recomputes the plan in the same transaction.
    const ok = await save.run(
      () => api.fpa.plans.setAssumptions(planId, entries),
      `${entries.length} driver value${entries.length === 1 ? '' : 's'}`,
    );
    if (!ok) return;   // keep the buffer so nothing typed is lost
    setPending({});
    asmptQ.refetch();
  }

  const groups = new Map<string, DriverDef[]>();
  for (const d of drivers) (groups.get(d.group) ?? groups.set(d.group, []).get(d.group)!).push(d);

  const cellStyle: React.CSSProperties = { padding: '2px 5px', textAlign: 'right', fontSize: 11, fontVariantNumeric: 'tabular-nums', borderBottom: '0.5px solid var(--color-border)' };
  const labelStyle: React.CSSProperties = { padding: '3px 8px', fontSize: 11, borderBottom: '0.5px solid var(--color-border)', position: 'sticky', left: 0, background: 'var(--color-surface)', zIndex: 1 };
  const cellInput: React.CSSProperties = { width: 58, fontSize: 11, textAlign: 'right', border: '0.5px solid var(--color-border)', borderRadius: 3, padding: '1px 4px', background: 'var(--color-surface)', color: 'var(--color-text)', fontVariantNumeric: 'tabular-nums' };

  return (
    <div style={{ borderBottom: '0.5px solid var(--color-border)' }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '8px 12px', flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12, fontWeight: 500 }}>Driver values</span>
        <span style={{ fontSize: 11, color: 'var(--color-text-muted)', marginLeft: 6 }}>Department</span>
        <Select value={dept} onChange={setDept}>
          <option value={DEFAULT_DEPT}>Default (all departments)</option>
          {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
        </Select>
        <span style={{ fontSize: 10, color: 'var(--color-text-muted)' }}>Set the Global cell to cover every month; a month cell overrides it.</span>
        <span style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
          <SaveStatus state={save} />
          {dirty && save.stage !== 'saving' && <span style={{ fontSize: 11, color: 'var(--color-amber)' }}>Unsaved</span>}
          {editableCanWrite && <ActionButton variant="primary" onClick={runSave} disabled={save.busy || !dirty}>{save.busy ? 'Saving…' : 'Save & recompute'}</ActionButton>}
        </span>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ borderCollapse: 'collapse', minWidth: '100%' }}>
          <thead>
            <tr>
              <th style={{ ...labelStyle, fontWeight: 600, textAlign: 'left', zIndex: 2 }}>Driver</th>
              <th style={{ ...cellStyle, fontWeight: 600 }}>Global</th>
              {months.map((m) => <th key={m} style={{ ...cellStyle, fontWeight: 600 }}>{m.slice(2)}</th>)}
            </tr>
          </thead>
          <tbody>
            {Array.from(groups.entries()).map(([grp, ds]) => (
              <Fragment key={grp}>
                <tr style={{ background: 'var(--color-surface-alt, rgba(120,120,120,0.05))' }}>
                  <td style={{ ...labelStyle, fontWeight: 600, background: 'var(--color-surface-alt, rgba(120,120,120,0.05))' }} colSpan={months.length + 2}>{grp}</td>
                </tr>
                {ds.map((d) => {
                  const companyLockedForDept = d.scope === 'company' && deptId !== null;
                  return (
                    <tr key={d.key}>
                      <td style={{ ...labelStyle }}>
                        <span title={d.description}>{d.label}</span>
                        <span style={{ fontSize: 9, marginLeft: 4, color: 'var(--color-text-muted)' }}>{unitSuffix(d.unit) || d.unit}{d.scope === 'company' ? ' · company' : ''}</span>
                        {editableCanWrite && !companyLockedForDept && <button onClick={() => fillRight(d)} title="Fill the Global value across all months" style={{ marginLeft: 6, fontSize: 10, background: 'none', border: 'none', color: 'var(--color-blue)', cursor: 'pointer' }}>⇥</button>}
                      </td>
                      <td style={cellStyle}>
                        {editableCanWrite && !companyLockedForDept
                          ? <input style={cellInput} value={rawFor(d, null)} placeholder={d.default_value != null ? toInputValue(d.unit, d.default_value) : ''} onChange={(e) => setCell(d, null, e.target.value)} />
                          : (companyLockedForDept ? <span style={{ color: 'var(--color-text-muted)' }} title="Company-level — edit under Default">{rawFor(d, null) || '—'}</span> : rawFor(d, null))}
                      </td>
                      {months.map((m) => (
                        <td key={m} style={cellStyle}>
                          {editableCanWrite && !companyLockedForDept
                            ? <input style={cellInput} value={rawFor(d, m)} placeholder={effective(d)} onChange={(e) => setCell(d, m, e.target.value)} />
                            : rawFor(d, m)}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
